import { ChangeEvent, ReactElement, useState } from 'react'
import { useIntl, defineMessages } from 'react-intl'
import styled from 'styled-components'
import isBefore from 'date-fns/isBefore'
import subDays from 'date-fns/subDays'
import startOfMonth from 'date-fns/startOfMonth'
import subMonths from 'date-fns/subMonths'
import format from 'date-fns/format'
import addDays from 'date-fns/addDays'

import { Input } from 'components/Input'
import Dropdown from 'components/v2/Dropdown'
import { Radio } from 'components/v2/Radio'
import { InputDate } from 'components/v2/InputDate'
import { dateRangeToString, DATE_FORMAT, sanitizeDate } from 'utils/date'
import { endOfDay, isAfter, startOfDay } from 'date-fns'
import { IDateRangeFilterProps, IDateRangeFilter, IDateRangeAsString, IDateRangeV2AsString } from './types'

const Wrapper = styled.div<TSFixMe>`
  padding: 10px;
  margin: -10px 0;
`

const Row = styled.div<TSFixMe>`
  margin: 10px 0;
`

export const DATE_RANGE = {
  TODAY: 'today',
  YESTERDAY: 'yesterday',
  SEVEN_DAYS: 'seven_days',
  THIS_MONTH: 'this_month',
  THIRTY_DAYS: 'thirty_days',
  THREE_MONTHS: 'three_months'
}

export const DATE_RANGE_LIST = [
  DATE_RANGE.TODAY,
  DATE_RANGE.YESTERDAY,
  DATE_RANGE.SEVEN_DAYS,
  DATE_RANGE.THIS_MONTH,
  DATE_RANGE.THIRTY_DAYS,
  DATE_RANGE.THREE_MONTHS
]

export const getDateRangeByRangeValue = (rangeValue: string): IDateRangeFilter => {
  let from, to, yesterday
  const today = new Date()

  switch (rangeValue) {
    case DATE_RANGE.TODAY:
      from = today
      to = today
      break
    case DATE_RANGE.YESTERDAY:
      yesterday = subDays(today, 1)
      from = yesterday
      to = yesterday
      break
    case DATE_RANGE.SEVEN_DAYS:
      from = subDays(today, 6)
      to = today
      break
    case DATE_RANGE.THIS_MONTH:
      from = startOfMonth(today)
      to = today
      break
    case DATE_RANGE.THIRTY_DAYS:
      from = subDays(today, 30)
      to = today
      break
    case DATE_RANGE.THREE_MONTHS:
      from = subMonths(today, 3)
      to = today
      break
  }

  return {
    from,
    to
  }
}

const messages = defineMessages({
  startsAtPlaceholder: {
    id: 'dateRange.startsAt.placeholder',
    defaultMessage: 'Select starting date'
  },
  endsAtPlaceholder: {
    id: 'dateRange.endsAt.placeholder',
    defaultMessage: 'Select ending date'
  }
})

const dateRangeMessages = defineMessages({
  [DATE_RANGE.TODAY]: {
    id: 'dateRange.value.today',
    defaultMessage: 'Today'
  },
  [DATE_RANGE.YESTERDAY]: {
    id: 'dateRange.value.yesterday',
    defaultMessage: 'Yesterday'
  },
  [DATE_RANGE.SEVEN_DAYS]: {
    id: 'dateRange.value.sevenDays',
    defaultMessage: 'Seven days'
  },
  [DATE_RANGE.THIS_MONTH]: {
    id: 'dateRange.value.thisMonth',
    defaultMessage: 'This month'
  },
  [DATE_RANGE.THIRTY_DAYS]: {
    id: 'dateRange.value.thirtyDays',
    defaultMessage: 'Thirty days'
  },
  [DATE_RANGE.THREE_MONTHS]: {
    id: 'dateRange.value.threeMonths',
    defaultMessage: 'Three months'
  }
})

export const getDateRange = (dateRange: IDateRangeFilter): IDateRangeAsString | null => {
  const from = (dateRange?.from && format(dateRange.from, DATE_FORMAT)) || null
  const to = (dateRange?.to && format(dateRange.to, DATE_FORMAT)) || null

  if (from && to) {
    return {
      from,
      to
    }
  } else if (from) {
    return {
      from
    }
  } else if (to) {
    return {
      to
    }
  }

  return null
}

export const getDateRangeV2 = (dateRange: IDateRangeFilter): IDateRangeV2AsString | null => {
  const startAt = dateRange?.from ? startOfDay(dateRange?.from as Date)?.toISOString() : null
  const endAt = dateRange?.to ? endOfDay(dateRange?.to as Date)?.toISOString() : null

  if (startAt && endAt) {
    return {
      startAt,
      endAt
    }
  } else if (startAt) {
    return {
      startAt
    }
  } else if (endAt) {
    return {
      endAt
    }
  }

  return null
}

export const sanitizeDateRange = (dateRange: IDateRangeAsString): IDateRangeFilter => {
  if (DATE_RANGE_LIST.indexOf(dateRange?.rangeValue || '') > -1) {
    return {
      ...getDateRangeByRangeValue(dateRange?.rangeValue || ''),
      rangeValue: DATE_RANGE_LIST.indexOf(dateRange?.rangeValue || '') > -1 ? dateRange?.rangeValue : ''
    }
  }
  return {
    from: sanitizeDate(dateRange?.from),
    to: sanitizeDate(dateRange?.to),
    rangeValue: ''
  }
}

export const DateRangeFilter = ({
  dateRangeDaysLimit,
  onChange,
  value,
  placeholder
}: IDateRangeFilterProps): ReactElement => {
  const intl = useIntl()

  const [isDropdownOpen, setDropdownOpen] = useState(false)

  const handleDateRangeChange = ({ target: { id: rangeValue } }: ChangeEvent): void => {
    onChange({
      rangeValue,
      ...getDateRangeByRangeValue(rangeValue)
    })
  }
  const handleFromChange = (newValue?: Date): void => {
    let { from, to } = value
    from = newValue

    if (to && from && isBefore(to, from)) {
      to = from
    } else if (from && dateRangeDaysLimit) {
      const dateLimit = addDays(from, dateRangeDaysLimit)
      if (to && isAfter(to, dateLimit)) {
        to = dateLimit
      }
    }

    onChange({
      rangeValue: '',
      from,
      to
    })
  }

  const handleToChange = (newValue?: Date): void => {
    let { from, to } = value
    to = newValue

    if (from && to && isBefore(to, from)) {
      from = to
    } else if (to && dateRangeDaysLimit) {
      const dateLimit = addDays(to, -dateRangeDaysLimit)
      if (from && isBefore(from, dateLimit)) {
        from = dateLimit
      }
    }

    onChange({
      rangeValue: '',
      from,
      to
    })
  }

  return (
    <Dropdown.Wrapper onClickAway={(): void => setDropdownOpen(false)} form>
      <Input
        value={dateRangeToString(intl, value)}
        onChange={(): void => {
          //Do nothing
        }}
        onClick={(): void => setDropdownOpen(!isDropdownOpen)}
        placeholder={placeholder}
      />
      <Dropdown.Content open={isDropdownOpen} right fitContent form noOverflow>
        <Wrapper>
          <Row>
            <InputDate
              intl={intl}
              value={value.from}
              onChange={handleFromChange}
              placeholder={intl.formatMessage(messages.startsAtPlaceholder)}
              optional
            />
          </Row>
          <Row>
            <InputDate
              intl={intl}
              value={value.to}
              onChange={handleToChange}
              placeholder={intl.formatMessage(messages.endsAtPlaceholder)}
              optional
            />
          </Row>
          {DATE_RANGE_LIST.map((dateRange, index): TSFixMe => {
            return (
              <Row key={index}>
                <Radio
                  id={dateRange}
                  name="dateRange"
                  checked={value.rangeValue === dateRange}
                  onChange={handleDateRangeChange}
                >
                  {intl.formatMessage(dateRangeMessages[dateRange])}
                </Radio>
              </Row>
            )
          })}
        </Wrapper>
      </Dropdown.Content>
    </Dropdown.Wrapper>
  )
}
